import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkle, Loader2, Play, Pause } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Keepsake = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/keepsake/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This keepsake link is invalid."));
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [token]);

  const setupAnalyser = () => {
    if (ctxRef.current || !data?.soundwave) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ac = new AudioCtx();
      const src = ac.createMediaElementSource(audioRef.current);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 128;
      src.connect(analyser);
      analyser.connect(ac.destination);
      ctxRef.current = ac;
      analyserRef.current = analyser;
    } catch (e) { console.error("Soundwave analyser setup failed (optional):", e); }
  };

  const drawWave = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const bins = analyser.frequencyBinCount;
    const arr = new Uint8Array(bins);
    const render = () => {
      analyser.getByteFrequencyData(arr);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const bars = 40;
      const step = Math.floor(bins / bars);
      const bw = canvas.width / bars;
      for (let i = 0; i < bars; i++) {
        const v = arr[i * step] / 255;
        const h = Math.max(4, v * canvas.height * 0.9);
        const x = i * bw;
        const grad = ctx.createLinearGradient(0, canvas.height, 0, canvas.height - h);
        grad.addColorStop(0, "#c9a24a");
        grad.addColorStop(1, "#8a6cae");
        ctx.fillStyle = grad;
        const y = (canvas.height - h) / 2;
        ctx.beginPath();
        ctx.roundRect(x + bw * 0.15, y, bw * 0.7, h, 4);
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(render);
    };
    render();
  };

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); return; }
    setupAnalyser();
    if (ctxRef.current?.state === "suspended") await ctxRef.current.resume();
    try { await el.play(); } catch (e) { console.error("Audio playback failed:", e); }
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onPlay = () => { setPlaying(true); if (data?.soundwave) drawWave(); };
    const onPause = () => { setPlaying(false); if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    el.addEventListener("play", onPlay); el.addEventListener("pause", onPause); el.addEventListener("ended", onPause);
    return () => { el.removeEventListener("play", onPlay); el.removeEventListener("pause", onPause); el.removeEventListener("ended", onPause); };
  }, [data]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-red-500 p-6" data-testid="keepsake-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading your keepsake…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="keepsake-page">
      <div className="w-full max-w-md surface rounded-[2rem] p-8 text-center animate-fade-up">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Sparkle className="w-5 h-5 text-white" /></div>
          <span className="gifted-brand text-2xl">GIFTED</span>
        </div>
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-2">Audio Keepsake</p>
        <h1 className="font-serif text-3xl mb-1">For {data.loved_one_name}</h1>
        <p className="text-muted-foreground mb-5">{data.occasion}</p>
        {data.image_base64 && (
          <div className="rounded-2xl overflow-hidden border border-border mb-5">
            <img src={`data:image/*;base64,${data.image_base64}`} alt="" className="w-full h-44 object-cover" />
          </div>
        )}

        {/* Soundwave (Royal) */}
        {data.soundwave ? (
          <canvas ref={canvasRef} width="380" height="90" data-testid="keepsake-soundwave"
            className="w-full h-[90px] mb-4" />
        ) : (
          <div className="mb-4 text-xs text-muted-foreground">A voice message awaits you.</div>
        )}

        <button data-testid="keepsake-play" onClick={toggle}
          className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary text-white flex items-center justify-center mx-auto hover:scale-105 transition-transform gold-pulse-ring">
          {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-0.5" />}
        </button>

        {data.message && <p className="font-serif italic text-lg mt-6">&ldquo;{data.message}&rdquo;</p>}

        <audio ref={audioRef} src={`data:${data.audio_mime};base64,${data.audio_base64}`} crossOrigin="anonymous" preload="auto" data-testid="keepsake-audio" />
      </div>
    </div>
  );
};

export default Keepsake;
